import { useState } from 'react';
import type { InventoryLevel } from '@interfaces/ILogistics';
import { zonedDay } from '../logistics.format';
import { readJsonPref, writeJsonPref } from '@utils/localJsonPref';
import { useNow } from '@hooks/useNow';

/** The dismissals stored under `key` — only well-formed entries survive. */
function readDismissed(key: string): ReadonlySet<string> {
  const raw = readJsonPref(key);
  return new Set(Array.isArray(raw) ? raw.filter((entry): entry is string => typeof entry === 'string') : []);
}

/**
 * Arrival prompts the user has answered "not yet" to, keyed by material and
 * the order the prompt is about. "Later" settles the prompt on the spot:
 * the row falls back to its plain ran-out state.
 *
 * PERSISTED (localStorage, per site) rather than session-local: a refresh is
 * not a fresh decision, so a dismissal that reappeared on reload read as
 * broken.
 *
 * But it is scoped to TODAY as well as the order (`dueKey` carries the day).
 * An order that is not in Delivered is still owed the "did it arrive?" ask,
 * and a "not yet" answered on the due date is stale the next morning — the
 * delivery is a day more overdue and even likelier to have landed. So the ask
 * returns each new day it stays overdue, while a same-day refresh keeps it
 * dismissed. (A NEW order for the material re-asks too — different ref.)
 */
export function useInventoryDueDismissed(siteId: string, tz: string) {
  const dueDismissKey = `dorca.logistics.dueDismissed.${siteId}`;
  const [dueDismissed, setDueDismissed] = useState<ReadonlySet<string>>(() => readDismissed(dueDismissKey));
  // The panel stays mounted across a site switch, so a new site re-reads its
  // own store — otherwise site A's set would show on B and the next dismiss
  // would write it under B's key. Adjusted during render (React's
  // "state from a changed prop" pattern), so no frame shows the stale set.
  const [loadedKey, setLoadedKey] = useState(dueDismissKey);
  if (loadedKey !== dueDismissKey) {
    setLoadedKey(dueDismissKey);
    setDueDismissed(readDismissed(dueDismissKey));
  }
  // Ticks, so a page left open sees the ask return at the site's midnight.
  const today = zonedDay(useNow(60_000).toISOString(), tz);
  const dueKey = (level: InventoryLevel) =>
    `${level.materialName}:${level.incomingRef ?? ''}:${today}`;
  const dismissDue = (level: InventoryLevel) =>
    setDueDismissed((prev) => {
      // Only today's entries can still match a `dueKey`, so drop older days as
      // we write — the store stays bounded instead of growing without end.
      const next = new Set([...prev].filter((entryKey) => entryKey.endsWith(`:${today}`))).add(dueKey(level));
      writeJsonPref(dueDismissKey, [...next]);
      return next;
    });

  return { dueDismissed, dueKey, dismissDue };
}
